import { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { Bell, User, Music, ListTodo, Phone, ClipboardList } from 'lucide-react';
import { format, addDays } from 'date-fns';
import type { SlimContact, Band } from '../../types';

interface TodoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TodoFormData) => void;
  venueName?: string;
  contacts?: SlimContact[];
  bands?: Band[];
  initialValues?: Partial<TodoFormData>;
  editMode?: boolean;
  optionalDescription?: boolean;
}

export type TodoType = 'remind' | 'contact' | 'task';

const TODO_TYPE_OPTIONS = [
  { value: 'remind', label: 'Remind', icon: Bell },
  { value: 'contact', label: 'Contact', icon: Phone },
  { value: 'task', label: 'Task', icon: ListTodo },
] as const;

// Remind / Contact todos get their type label pre-filled as the description; it is
// cleared on the first click/focus so typing over it needs no manual deletion.
const AUTOFILL_TYPES: readonly TodoType[] = ['remind', 'contact'];

const autofillDescription = (type: TodoType) =>
  AUTOFILL_TYPES.includes(type) ? TODO_TYPE_OPTIONS.find((o) => o.value === type)!.label : '';

export interface TodoFormData {
  description: string;
  datetime: string;
  type: TodoType;
  contact_id?: string;
  band_ids?: string[];
}

export function TodoModal({
  isOpen,
  onClose,
  onSubmit,
  venueName,
  contacts = [],
  bands = [],
  initialValues,
  editMode = false,
  optionalDescription = false,
}: TodoModalProps) {
  const [description, setDescription] = useState('');
  const [datetime, setDatetime] = useState('');
  const [todoType, setTodoType] = useState<TodoType>('task');
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [selectedBandIds, setSelectedBandIds] = useState<string[]>([]);
  const [selectedQuickDate, setSelectedQuickDate] = useState<number | null>(null);
  const [isDescriptionAutofilled, setIsDescriptionAutofilled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDatetime(initialValues?.datetime ? format(new Date(initialValues.datetime), "yyyy-MM-dd'T'HH:mm") : format(new Date(), "yyyy-MM-dd'T'HH:mm"));
      const type = initialValues?.type || 'contact';
      setTodoType(type);
      const prefill = !initialValues?.description ? autofillDescription(type) : '';
      setDescription(initialValues?.description || prefill);
      setIsDescriptionAutofilled(prefill !== '');
      setSelectedContactId(initialValues?.contact_id || '');
      setSelectedBandIds(initialValues?.band_ids || []);
      setSelectedQuickDate(null);
    }
  }, [isOpen, initialValues]);

  // Description is mandatory for Task only: Remind / Contact fall back to their type label.
  const descriptionRequired = !optionalDescription && todoType === 'task';

  const handleTypeChange = (type: TodoType) => {
    setTodoType(type);
    // Only overwrite a description the user has not typed themselves.
    if (!description.trim() || isDescriptionAutofilled) {
      const prefill = autofillDescription(type);
      setDescription(prefill);
      setIsDescriptionAutofilled(prefill !== '');
    }
  };

  const clearAutofilledDescription = () => {
    if (!isDescriptionAutofilled) return;
    setDescription('');
    setIsDescriptionAutofilled(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((descriptionRequired && !description.trim()) || !datetime) return;
    const typeLabel = TODO_TYPE_OPTIONS.find((o) => o.value === todoType)?.label ?? '';
    onSubmit({
      description: description.trim() || typeLabel,
      datetime: new Date(datetime).toISOString(),
      type: todoType,
      contact_id: selectedContactId || undefined,
      band_ids: selectedBandIds.length > 0 ? selectedBandIds : undefined,
    });
    onClose();
  };

  const setQuickDate = (days: number) => {
    const date = days === 0 ? new Date() : addDays(new Date(), days);
    if (days !== 0) date.setHours(10, 30, 0, 0);
    setDatetime(format(date, "yyyy-MM-dd'T'HH:mm"));
    setSelectedQuickDate(days);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${editMode ? 'Edit' : 'New'} Todo${venueName ? ` - ${venueName}` : ''}`}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-dark-text-secondary mb-1">
            <div className="flex items-center gap-1">
              <ClipboardList className="w-3 h-3" />
              Type
            </div>
          </label>
          <div className="flex gap-2">
            {TODO_TYPE_OPTIONS.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleTypeChange(value)}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded transition-colors ${
                  todoType === value
                    ? 'bg-primary-600 border border-primary-500 text-white'
                    : 'bg-dark-elevated border border-dark-border hover:bg-dark-border text-dark-text-secondary'
                }`}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            ))}
          </div>
        </div>

        <Input
          label={descriptionRequired ? 'Description' : 'Description (optional)'}
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            setIsDescriptionAutofilled(false);
          }}
          onFocus={clearAutofilledDescription}
          onMouseDown={clearAutofilledDescription}
          placeholder="What do you need to do?"
          required={descriptionRequired}
        />

        <div>
          <label className="block text-xs font-medium text-dark-text-secondary mb-1">
            When
          </label>
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              onClick={() => setQuickDate(0)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                selectedQuickDate === 0
                  ? 'bg-primary-600 border border-primary-500 text-white'
                  : 'bg-dark-elevated border border-dark-border hover:bg-dark-border'
              }`}
            >
              Now
            </button>
            <button
              type="button"
              onClick={() => setQuickDate(7)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                selectedQuickDate === 7
                  ? 'bg-primary-600 border border-primary-500 text-white'
                  : 'bg-dark-elevated border border-dark-border hover:bg-dark-border'
              }`}
            >
              1 week
            </button>
            <button
              type="button"
              onClick={() => setQuickDate(14)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                selectedQuickDate === 14
                  ? 'bg-primary-600 border border-primary-500 text-white'
                  : 'bg-dark-elevated border border-dark-border hover:bg-dark-border'
              }`}
            >
              2 weeks
            </button>
            <button
              type="button"
              onClick={() => setQuickDate(30)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                selectedQuickDate === 30
                  ? 'bg-primary-600 border border-primary-500 text-white'
                  : 'bg-dark-elevated border border-dark-border hover:bg-dark-border'
              }`}
            >
              1 month
            </button>
          </div>
          <input
            type="datetime-local"
            value={datetime}
            onChange={(e) => {
              setDatetime(e.target.value);
              setSelectedQuickDate(null);
            }}
            className="w-full px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-dark-text-primary text-sm focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        {/* Contact Selector (optional) */}
        {contacts.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-1">
              <div className="flex items-center gap-1">
                <User className="w-3 h-3" />
                Contact (optional)
              </div>
            </label>
            <select
              value={selectedContactId}
              onChange={(e) => setSelectedContactId(e.target.value)}
              className="w-full px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-dark-text-primary text-sm focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            >
              <option value="">No specific contact</option>
              {contacts.map((contact) => {
                const displayName = contact.name || contact.email || contact.phone || 'Unknown';
                return (
                  <option key={contact._id} value={contact._id}>
                    {displayName}{contact.function ? ` (${contact.function})` : ''}
                  </option>
                );
              })}
            </select>
            <p className="text-xs text-dark-text-muted mt-1">
              Optionally associate this todo with a specific contact
            </p>
          </div>
        )}

        {/* Band Selector (optional) */}
        {bands.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-1">
              <div className="flex items-center gap-1">
                <Music className="w-3 h-3" />
                Bands (optional)
              </div>
            </label>
            <div className="flex flex-wrap gap-2 p-2 bg-dark-elevated border border-dark-border rounded-lg min-h-[42px]">
              {bands.map((band) => {
                const isSelected = selectedBandIds.includes(band._id);
                return (
                  <button
                    key={band._id}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        setSelectedBandIds(selectedBandIds.filter((id) => id !== band._id));
                      } else {
                        setSelectedBandIds([...selectedBandIds, band._id]);
                      }
                    }}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      isSelected
                        ? 'bg-primary-600 border border-primary-500 text-white'
                        : 'bg-dark-surface border border-dark-border hover:bg-dark-border text-dark-text-secondary'
                    }`}
                  >
                    {band.name}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-dark-text-muted mt-1">
              Optionally associate this todo with specific bands
            </p>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button type="submit">
            <Bell className="w-4 h-4" />
            {editMode ? 'Save Todo' : 'Create Todo'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
